// JQ4-19 slice 3 — useCompsConfig
//
// Returns the comps snapshot for the active location by reusing the
// checkin-form-config endpoint. Uses TanStack Query so the response is
// cached per location and not refetched on every render.
//
// comps.enabled      → whether comps are enabled at this location
// comps.authorizedForActor → enabled AND the JWT role is in compAuthorizedRoles
//                            (evaluated server-side — no role check in the PWA)

import { getCheckinFormConfig } from '@jq4race/api-client';
import type { CompsConfigSnapshot } from '@jq4race/shared';
import { useQuery } from '@tanstack/react-query';

const ACTIVE_LOCATION_KEY = 'jq4race:active_location_id';

const DISABLED: CompsConfigSnapshot = { enabled: false, authorizedForActor: false };

export function compsConfigKey(locationId: string) {
  return ['comps-config', locationId] as const;
}

/**
 * useCompsConfig — returns { comps, isLoading } for the active location.
 *
 * Falls back to DISABLED when the location is unknown or the fetch fails, so
 * the comp button simply stays hidden rather than crashing the UI.
 */
export function useCompsConfig(): { comps: CompsConfigSnapshot; isLoading: boolean } {
  const locationId =
    typeof localStorage !== 'undefined' ? localStorage.getItem(ACTIVE_LOCATION_KEY) : null;

  const query = useQuery({
    queryKey: compsConfigKey(locationId ?? ''),
    queryFn: () => getCheckinFormConfig(locationId ?? ''),
    // Only run when we have a location ID
    enabled: locationId !== null,
    // Config is stable per session — no need to refetch frequently.
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Allowlisted for offline persistence (task 3.5, app/query-client.ts).
    networkMode: 'offlineFirst',
    select: (data) => data.comps,
  });

  return {
    comps: query.data ?? DISABLED,
    isLoading: query.isLoading,
  };
}
